import React, { useState, useRef, useEffect } from 'react';
import EntityTable, { ENTITY_TABLE_PAGE_SIZE } from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTable';
import { GeneratingModal } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/GeneratingModal';
import color from 'src/utils/colors';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { SearchTextField } from 'src/components/BeaconV2Components/TableSearch';
import {
  useProductTable,
  type ProductTableStoreOverride,
  type MergeQueryFn,
  type MergeQueryParams
} from '../ProductTable/useProductTable';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { useContentGenerationStore } from '../ProductTable/store';
import { GenerationStatus } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Generate/types';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';
import { AppFilterButton } from 'src/components/BeaconV2Components/common/AppFilterButton/AppFilterButton';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { Box } from '@mui/material';
import { RetailerSkuBulkUploadModal } from './RetailerSkuBulkUploadModal';
import { useAddProductsModalFilters } from '../../../Project/AddProductsModalContext';
import { SlPill } from 'src/components/BeaconV2Components/SlPill';

export const GENERATION_LIMIT = 20;
export const MODAL_PAGE_SIZE = 4;

interface ProductTableProps {
  retailerSkus?: string[];
  variant?: 'default' | 'review' | 'edit' | 'modal';
  overrideStatus?: GenerationStatus;
  projectId?: string | null;
  excludeRetailerSkus?: string[];
  overrideAppFilters?: ReturnType<
    typeof import('src/utils/Hooks/useDorisApplyEntityFilters').useBeaconFilters
  >['appFilters'];
  overrideStore?: ProductTableStoreOverride;
  mergeQueryFn?: MergeQueryFn;
  mergeQueryOverrides?: Partial<Pick<MergeQueryParams, 'retailerSkus' | 'excludeRetailerSkus'>>;
  onEnableFinalReview?: () => void;
  /** When true, the internal GeneratingModal is not rendered (use when parent renders its own) */
  hideGeneratingModal?: boolean;
}

const ProductTable: React.FC<ProductTableProps> = ({
  retailerSkus,
  variant = 'default',
  overrideStatus,
  projectId,
  excludeRetailerSkus,
  overrideAppFilters,
  overrideStore,
  mergeQueryFn,
  mergeQueryOverrides,
  onEnableFinalReview,
  hideGeneratingModal = false
}) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [bulkUploadSkus, setBulkUploadSkus] = useState<string[] | undefined>(retailerSkus);
  // For modal variant: track how many items to show (starts at 4, increments by 4)
  const [visibleItemsCount, setVisibleItemsCount] = useState(MODAL_PAGE_SIZE);
  // Track if initial data has loaded - after this, we skip loading state to prevent flickering
  const [hasInitialDataLoaded, setHasInitialDataLoaded] = useState(false);

  // Try to get context filters - will return undefined if not within context provider
  let contextFilters;
  let selectedBrandIds: Array<string | number> | undefined;
  try {
    const modalContext = useAddProductsModalFilters();
    const overrideFilters = modalContext?.getOverrideAppFilters();
    contextFilters = overrideFilters;
    selectedBrandIds = modalContext?.selectedBrandIds;
  } catch (error) {
    contextFilters = undefined;
    selectedBrandIds = undefined;
  }

  const effectiveOverrideFilters = contextFilters || overrideAppFilters;

  const {
    allData,
    columnDefs,
    searchQuery,
    setSearchQuery,
    selectedCount,
    isLoading,
    isError,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useProductTable({
    retailerSkus: React.useMemo(() => {
      const set = new Set<string>();
      (retailerSkus || []).forEach((s) => set.add(s));
      (bulkUploadSkus || []).forEach((s) => set.add(s));
      return Array.from(set);
    }, [retailerSkus, bulkUploadSkus]),
    variant,
    overrideStatus,
    projectId,
    excludeRetailerSkus,
    overrideAppFilters: effectiveOverrideFilters,
    overrideStore,
    mergeQueryFn,
    mergeQueryOverrides,
    onEnableFinalReview,
    currentPageNumber: variant === 'modal' ? 1 : pageNumber
  });

  const { isGeneratingModalOpen, closeGeneratingModal } = useContentGenerationStore();

  const itemsPerPage = ENTITY_TABLE_PAGE_SIZE;
  const brandIdSet = React.useMemo(() => {
    if (!selectedBrandIds || selectedBrandIds.length === 0) return null;
    return new Set(selectedBrandIds.map((id) => Number(id)));
  }, [selectedBrandIds]);

  const allDataFiltered = React.useMemo(() => {
    if (!brandIdSet) return allData;
    return allData.filter((item) => {
      const brandId = item.product?.brandId;
      return typeof brandId === 'number' ? brandIdSet.has(brandId) : false;
    });
  }, [allData, brandIdSet]);

  const currentPageData = React.useMemo(() => {
    // For modal variant, show all items up to visibleItemsCount (no pagination)
    if (variant === 'modal') {
      return allDataFiltered;
    }
    // For other variants, use pagination
    const startIndex = (pageNumber - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return allDataFiltered.slice(startIndex, endIndex);
  }, [allDataFiltered, pageNumber, itemsPerPage, variant]);

  // Set hasInitialDataLoaded to true once data first loads
  // After this, we skip loading state to prevent flickering on subsequent data changes
  useEffect(() => {
    if (!hasInitialDataLoaded && allData.length > 0) {
      setHasInitialDataLoaded(true);
    }
  }, [allData, hasInitialDataLoaded]);

  // Cache previous data to prevent empty table after initial load
  const cachedDataRef = useRef<typeof currentPageData>([]);
  
  // When initial data has loaded and data becomes empty, use cached data
  const effectiveRowData = React.useMemo(() => {
    if (hasInitialDataLoaded && currentPageData.length === 0 && cachedDataRef.current.length > 0) {
      return cachedDataRef.current;
    }
    // Update cache when we have valid data
    if (currentPageData.length > 0) {
      cachedDataRef.current = currentPageData;
    }
    return currentPageData;
  }, [currentPageData, hasInitialDataLoaded]);

  const totalLoadedPages = Math.ceil(allDataFiltered.length / itemsPerPage);
  const canGoToNextPage = pageNumber < totalLoadedPages || hasNextPage;

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleBulkUploadSave = (skus: string[]) => {
    setBulkUploadSkus(skus);
    setPageNumber(1);
  };

  if (isError) {
    return (
      <>
        <Flex flexDirection="column" gap={16} alignItems="center" justifyContent="center" height="300px">
          <Text color="error" variant="body1">
            Error loading product data
          </Text>
          <button
            onClick={() => refetch()}
            style={{
              padding: '8px 16px',
              backgroundColor: color.darkBlue,
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </Flex>
      </>
    );
  }

  return (
    <>
      <Flex flexDirection="column" gap={32}>
        <Flex justifyContent="space-between" alignItems="center">
          <SearchTextField placeholder="Search products" value={searchQuery} onChange={handleSearchChange} />
          {variant === 'default' && (
            <Flex gap={16}>
              {selectedCount > 0 && (
                <SlPill
                  sx={{
                    cursor: 'default',
                    backgroundColor: color.graysPrimary,
                    color: color.darkBlue,
                    borderColor: color.graysSecondary
                  }}
                >
                  {selectedCount} selected
                </SlPill>
              )}

              <SlButton onClick={() => setIsBulkUploadModalOpen(true)} variant="contained">
                Bulk Upload
              </SlButton>
              <AppFilterButton />
            </Flex>
          )}
        </Flex>
        <Box
          width="100%"
          sx={{
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            th: {
              whiteSpace: 'nowrap'
            },
            // For modal variant, make table scrollable with sticky header
            ...(variant === 'modal' && {
              maxHeight: '290px',
              overflowY: 'auto',
              overflowX: 'hidden',
              'thead': {
                position: 'sticky',
                top: 0,
                zIndex: 10,
                backgroundColor: '#fff'
              },
              '&::-webkit-scrollbar': {
                width: '6px'
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
                marginTop: '44px' // Offset scrollbar to start below header
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '3px',
                '&:hover': {
                  background: 'rgba(0, 0, 0, 0.3)'
                }
              }
            })
          }}
        >
          <EntityTable
            rowData={effectiveRowData}
            columnDefs={columnDefs}
            shouldModifyColumnDefs={false}
            showPagination={false}
            initialPageSize={variant === 'modal' ? visibleItemsCount : itemsPerPage}
            isLoading={hasInitialDataLoaded ? false : (isLoading || isFetchingNextPage)}
            skeletonRows={20}
            appTableProps={variant === 'modal' ? { pageSize: visibleItemsCount } : undefined}
          />
          {/* For non-modal variants, show pagination inside the box */}
          {variant !== 'modal' && (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Pagination
                  currentPage={pageNumber}
                  shouldUseInfinitePagination={true}
                  onClickNext={() => {
                    const nextPage = pageNumber + 1;
                    setPageNumber(nextPage);

                    // Check if we need to fetch more data
                    const totalLoadedItems = allData.length;
                    const requiredItems = nextPage * itemsPerPage;

                    if (hasNextPage) {
                      fetchNextPage();
                    }
                  }}
                  disableNextButton={!canGoToNextPage || isFetchingNextPage}
                  onClickPrevious={() => {
                    if (pageNumber > 1) {
                      setPageNumber(pageNumber - 1);
                    }
                  }}
                />
              </div>
          )}
        </Box>
        {/* For modal variant, show "Show more" outside the scrollable box */}
        {variant === 'modal' && (allDataFiltered.length > visibleItemsCount || hasNextPage) && (
          <button
            style={{
              paddingLeft: 0,
              border: 'none',
              background: 'none',
              textAlign: 'left',
              cursor: 'pointer'
            }}
            onClick={() => {
              const newVisibleCount = visibleItemsCount + MODAL_PAGE_SIZE;
              setVisibleItemsCount(newVisibleCount);

              // Check if we need to fetch more data
              if (newVisibleCount > allData.length && hasNextPage) {
                fetchNextPage();
              }
            }}
            disabled={isFetchingNextPage}
          >
            <Text variant="subtitle2" underlined>
              {isFetchingNextPage ? 'Loading...' : 'Show more'}
            </Text>
          </button>
        )}
        {!hideGeneratingModal && (
          <GeneratingModal open={isGeneratingModalOpen} onClose={closeGeneratingModal} projectId={projectId} />
        )}
        <RetailerSkuBulkUploadModal
          open={isBulkUploadModalOpen}
          onClose={() => setIsBulkUploadModalOpen(false)}
          onSave={handleBulkUploadSave}
        />
      </Flex>
    </>
  );
};

export default React.memo(ProductTable);
